import { supabase } from '../lib/supabase';

/**
 * MP3 Memorization Service
 *
 * Loads the chapter MP3 and its allMarks (verse + chunk marks) from Supabase
 * and provides a playSegment() helper that seeks the audio element to a start
 * time and stops it at an end time — enabling MP3-based progressive chunking.
 *
 * The chunk marks in the DB use 3-word granularity (c2 = words 1-3, c3 = words 1-6, etc.)
 * For Neural2 mode the chunk size is always a multiple of 3 (3, 6, 9 words).
 */

// ── Data loading ─────────────────────────────────────────────────────────────

/**
 * Load the signed MP3 URL and all marks for a chapter/voice combo.
 * Returns null if no audio file found.
 *
 * @param {string} book   - e.g. "GEN"
 * @param {number} chapter
 * @param {string} voice  - e.g. "en-US-Neural2-J"
 * @returns {Promise<{url: string, verseMarks: Mark[], allMarks: Mark[]} | null>}
 */
export async function loadChapterAudio(book, chapter, voice) {
  // 1. Get audio file record
  const { data: fileData, error: fileErr } = await supabase?.from('bible_audio_files')?.select('id, mp3_path')?.eq('book', book)?.eq('chapter', chapter)?.eq('voice', voice)?.single();

  if (fileErr || !fileData) return null;

  // 2. Get signed URL
  const { data: signedData, error: signErr } = await supabase?.storage?.from('bible-audio')?.createSignedUrl(fileData?.mp3_path, 3600);

  if (signErr || !signedData) return null;

  // 3. Get all marks (verse + chunk)
  const { data: rawMarks, error: marksErr } = await supabase?.from('bible_audio_marks')?.select('ref, verse, is_chunk_mark, time_seconds')?.eq('audio_file_id', fileData?.id)?.order('time_seconds', { ascending: true });

  if (marksErr) return null;

  const marks = (rawMarks || [])?.sort((a, b) => a?.time_seconds - b?.time_seconds);
  const verseMarks = marks?.filter(m => !m?.is_chunk_mark);
  const allMarks = marks; // includes both verse-start and chunk marks

  return {
    url: signedData?.signedUrl,
    verseMarks,
    allMarks,
  };
}

/**
 * Get available Neural2 voices for a book/chapter.
 * Filters to only Neural2 voices (contain "Neural2").
 */
export async function getNeural2Voices(book, chapter) {
  const { data, error } = await supabase?.from('bible_audio_files')?.select('voice')?.eq('book', book)?.eq('chapter', chapter);

  if (error || !data) return [];
  const voices = [...new Set(data.map(r => r.voice))];
  return voices?.filter(v => v?.includes('Neural2'));
}

// ── Mark helpers ─────────────────────────────────────────────────────────────

/**
 * Given allMarks for a chapter and a verse number (and optional verseEnd for ranges),
 * return the marks relevant to that verse (or range) in time order.
 *
 * verseStart mark: ref = "BOOK.CH.V"   (is_chunk_mark = false)
 * chunk marks:     ref = "BOOK.CH.V.cN" (is_chunk_mark = true)
 * verseEnd:        the start time of the verse AFTER verseEnd (or end of audio)
 *
 * @param {Mark[]} allMarks
 * @param {number} verseNum       - start verse number
 * @param {number} audioDuration  - total duration in seconds (fallback for last verse)
 * @param {number} [verseEndNum]  - end verse number for ranges (defaults to verseNum)
 * @returns {{ verseStartTime: number, chunkMarks: ChunkMark[], verseEndTime: number }}
 */
export function getVerseMarksForVerse(allMarks, verseNum, audioDuration, verseEndNum) {
  const endVerse = verseEndNum != null ? verseEndNum : verseNum;

  // Find the verse-start mark for the first verse
  const verseStartMark = allMarks?.find(
    m => !m?.is_chunk_mark && m?.verse === verseNum
  );
  if (!verseStartMark) return null;

  const verseStartTime = verseStartMark?.time_seconds;

  // Find the next verse-start mark AFTER the end verse (determines where the range ends)
  const sortedVerseMarks = allMarks?.filter(m => !m?.is_chunk_mark)?.sort((a, b) => a?.time_seconds - b?.time_seconds);

  const endVerseIdx = sortedVerseMarks?.findIndex(m => m?.verse === endVerse);
  const nextVerseMark = endVerseIdx >= 0 ? sortedVerseMarks?.[endVerseIdx + 1] : null;
  const verseEndTime = nextVerseMark ? nextVerseMark?.time_seconds : (audioDuration || verseStartTime + 60);

  // Collect chunk marks for ALL verses in the range, sorted by time
  const chunkMarks = allMarks?.filter(m => {
    if (!m?.is_chunk_mark) return false;
    return m?.verse >= verseNum && m?.verse <= endVerse;
  })?.sort((a, b) => a?.time_seconds - b?.time_seconds);

  return { verseStartTime, chunkMarks, verseEndTime };
}

/**
 * Build the progressive chunk sequence for a verse given the chunk size (words per chunk).
 * Chunk size must be a multiple of 3 (3, 6, 9...).
 *
 * The marks in the DB are at every 3 words:
 *   verseStart = words 0
 *   c2         = words 3
 *   c3         = words 6
 *   c4         = words 9
 *   ...
 *
 * For chunkSize=3:  reveal c2, c3, c4, ... one at a time
 * For chunkSize=6:  reveal c3, c5, c7, ... (every 2nd mark)
 * For chunkSize=9:  reveal c4, c7, c10, ... (every 3rd mark)
 *
 * Each chunk plays from verseStartTime → stopTime.
 * Final chunk plays from verseStartTime → verseEndTime.
 *
 * @param {number} verseStartTime
 * @param {ChunkMark[]} chunkMarks  - sorted chunk marks for this verse
 * @param {number} verseEndTime
 * @param {number} chunkSize        - words per chunk (multiple of 3)
 * @returns {ChunkStep[]}
 */
export function buildChunkSteps(verseStartTime, chunkMarks, verseEndTime, chunkSize) {
  const step = Math.max(1, Math.round(chunkSize / 3)); // how many 3-word marks to skip
  const steps = [];

  // chunkMarks[0] = c2 (end of first 3 words)
  // chunkMarks[1] = c3 (end of first 6 words)
  // etc.
  // We pick every `step`-th mark as the stop point
  for (let i = step - 1; i < chunkMarks?.length; i += step) {
    steps?.push({
      stopTime: chunkMarks?.[i]?.time_seconds,
      wordCount: (i + 1) * 3, // approximate
      isComplete: false,
    });
  }

  // Always add the final full-verse step
  steps?.push({
    stopTime: verseEndTime,
    wordCount: null, // full verse
    isComplete: true,
  });

  return steps;
}

// ── Playback controller ───────────────────────────────────────────────────────

/**
 * Play a segment of an HTML audio element from startTime to stopTime.
 * Calls onEnded when the stop time is reached or the audio naturally ends.
 *
 * Returns a cleanup function that cancels the segment.
 *
 * @param {HTMLAudioElement} audioEl
 * @param {number} startTime
 * @param {number} stopTime
 * @param {function} onEnded
 * @param {number} [playbackRate=1.0]  - playback speed (e.g. 0.8 for 80%)
 * @returns {function} cleanup
 */
export function playSegment(audioEl, startTime, stopTime, onEnded, playbackRate = 1.0) {
  if (!audioEl) return () => {};

  let cancelled = false;
  let rafId = null;

  // Declare handleEnded before cleanup so it's in scope
  const handleEnded = () => {
    if (!cancelled) {
      cleanup();
      if (onEnded) onEnded();
    }
  };

  const cleanup = () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
    audioEl?.removeEventListener('ended', handleEnded);
    audioEl?.pause();
  };

  // Poll via requestAnimationFrame for precise stop
  const checkTime = () => {
    if (cancelled) return;
    if (audioEl?.currentTime >= stopTime) {
      audioEl?.pause();
      cleanup();
      if (onEnded) onEnded();
      return;
    }
    rafId = requestAnimationFrame(checkTime);
  };

  audioEl?.addEventListener('ended', handleEnded, { once: true });
  audioEl.currentTime = startTime;
  // Apply playback rate before playing
  audioEl.playbackRate = playbackRate > 0 ? playbackRate : 1.0;
  audioEl?.play()?.then(() => {
      if (!cancelled) {
        rafId = requestAnimationFrame(checkTime);
      }
    })?.catch(() => {
      if (!cancelled && onEnded) onEnded();
    });

  return cleanup;
}
